import { ActionType, CalendarDate } from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import { useQuery } from "@tanstack/react-query";
import type { inferRouterOutputs } from "@trpc/server";
import { Error } from "components/error";
import { ContentContainer, PageHeader, Stage } from "components/layout";
import { DateRangeControl } from "components/reports/date-presets";
import { trpc } from "components/rpc/trpc";
import { defaultCardStyle } from "components/ui/card";
import { Checkbox } from "components/ui/checkbox";
import { SortDown, SortUp } from "components/ui/icon";
import { SearchInput } from "components/ui/search-input";
import { Select } from "components/ui/select";
import { Spinner } from "components/ui/spinner";
import React, { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
    useFeature,
    useIsAllowedInWholeTenant,
    useRegionId,
} from "store/selectors/hooks";
import { franchiseRoyaltyReportSlice } from "store/slices/franchise-royalty-report";

type RouterOutput = inferRouterOutputs<AppRouter>;
type Franchisee =
    RouterOutput["franchiseRoyaltyReport"]["franchiseRoyaltyReport"]["franchisees"][number];

type InvoiceDetail = Franchisee["invoices"][number];

type SortField =
    | "regionName"
    | "territoryCode"
    | "userCount"
    | "paidInvoiceCount"
    | "collectedCents"
    | "avgPerInvoice"
    | "royaltyOwedCents";

const statusBadgeClasses: Record<string, string> = {
    paid: "bg-green-100 text-green-700",
    sent: "bg-blue-100 text-blue-700",
    new: "bg-amber-100 text-amber-700",
    void: "bg-red-100 text-red-700",
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
    const classes = statusBadgeClasses[status] ?? "bg-gray-100 text-gray-700";
    return (
        <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${classes}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-current" />
            {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
    );
};

const formatDate = (date: string | null): string => {
    if (!date) return "—";
    const [year, month, day] = date.split("-");
    if (!year || !month || !day) return date;
    return `${parseInt(month, 10)}/${parseInt(day, 10)}/${year}`;
};

const humanReadableMode = (mode: string): string => {
    if (mode === "FlatFee") return "Min Fee";
    if (mode === "PercentPlusMinimum") return "Percent / Minimum";
    return mode;
};

const formatCurrency = (cents: number): string => {
    return (cents / 100).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

const getAvgPerInvoice = (f: Franchisee): number => {
    return f.paidInvoiceCount > 0 ? Math.round(f.collectedCents / f.paidInvoiceCount) : 0;
};

const formatFeeStructureLine = (
    item: Franchisee["feeStructureBreakdown"][number],
): string => {
    const rate =
        item.rateBasisPoints !== null && item.rateBasisPoints > 0
            ? `${(item.rateBasisPoints / 100).toFixed(0)}%`
            : null;
    const lastMonth =
        item.monthlyDetails.length > 0
            ? item.monthlyDetails[item.monthlyDetails.length - 1]
            : null;
    const currentMinimum = lastMonth ? lastMonth.minimumFeeCents : item.minimumFeeCents;
    const minFormatted =
        currentMinimum > 0 && currentMinimum % 100 === 0
            ? `$${(currentMinimum / 100).toLocaleString("en-US")}`
            : formatCurrency(currentMinimum);
    const min = currentMinimum > 0 ? `${minFormatted} min` : null;
    const parts: string[] = [];
    if (rate && min) parts.push(`${rate} / ${min}`);
    else if (rate) parts.push(rate);
    else if (min) parts.push(min);
    else parts.push("\u2014");
    if (item.nextMinimumEffectiveDate && !min) {
        parts.push(
            `(${formatCurrency(item.nextMinimumAmountCents ?? 0)} min starts ${formatDate(item.nextMinimumEffectiveDate)})`,
        );
    }
    return parts.join(" ");
};

const isUnderMinimum = (f: Franchisee): boolean => {
    if (!f.hasFeeStructures || f.feeStructureBreakdown.length === 0) return false;
    return f.feeStructureBreakdown.every(item => {
        if (item.monthlyDetails.length > 0) {
            return item.monthlyDetails.every(d => d.appliedMethod === "minimum");
        }
        return item.appliedMethod === "minimum";
    });
};

const FeeStructureSummary: React.FC<{ franchisee: Franchisee }> = ({ franchisee }) => {
    if (!franchisee.hasFeeStructures) {
        return <span className="text-gray-400 text-xs">Not configured</span>;
    }
    if (franchisee.feeStructureBreakdown.length === 0) {
        return <span className="text-gray-400">{"\u2014"}</span>;
    }
    return (
        <div className="space-y-0.5">
            {franchisee.feeStructureBreakdown.map((item, idx) => (
                <div key={idx} className="text-xs leading-tight">
                    <span className="font-medium text-gray-700">{item.name}:</span>{" "}
                    <span className="text-gray-500">{formatFeeStructureLine(item)}</span>
                </div>
            ))}
        </div>
    );
};

const formatMonthLabel = (month: string): string => {
    const [year, m] = month.split("-");
    const date = new Date(parseInt(year, 10), parseInt(m, 10) - 1);
    return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
};

const hasMultipleMonths = (franchisee: Franchisee): boolean => {
    return franchisee.feeStructureBreakdown.some(item => item.monthlyDetails.length > 1);
};

const thClass =
    "text-xs font-semibold uppercase tracking-wide text-gray-500 py-1 px-2.5 border-b border-gray-200";

const SingleMonthBreakdownTable: React.FC<{ franchisee: Franchisee }> = ({
    franchisee,
}) => (
    <table className="w-full mb-4">
        <thead>
            <tr>
                <th className={`${thClass} text-left`}>Name</th>
                <th className={`${thClass} text-left`}>Mode</th>
                <th className={`${thClass} text-right`}>Rate</th>
                <th className={`${thClass} text-right`}>Min Fee</th>
                <th className={`${thClass} text-right`}>Calculated Royalty</th>
                <th className={`${thClass} text-left`}>Applied</th>
            </tr>
        </thead>
        <tbody>
            {franchisee.feeStructureBreakdown.map((item, idx) => (
                <tr key={idx} className="hover:bg-blue-50/20">
                    <td className="py-1.5 px-2.5 text-xs font-medium">{item.name}</td>
                    <td className="py-1.5 px-2.5 text-xs text-gray-600">
                        {humanReadableMode(item.mode)}
                    </td>
                    <td className="py-1.5 px-2.5 text-xs text-right">
                        {item.rateBasisPoints !== null
                            ? `${(item.rateBasisPoints / 100).toFixed(0)}%`
                            : "\u2014"}
                    </td>
                    <td className="py-1.5 px-2.5 text-xs text-right">
                        {item.minimumFeeCents > 0
                            ? formatCurrency(item.minimumFeeCents)
                            : "\u2014"}
                    </td>
                    <td className="py-1.5 px-2.5 text-xs text-right font-semibold text-purple-700">
                        {formatCurrency(item.calculatedRoyaltyCents)}
                    </td>
                    <td className="py-1.5 px-2.5 text-xs text-gray-600">
                        {item.appliedMethod === "percent"
                            ? item.nextMinimumEffectiveDate
                                ? `Percentage applied (${formatCurrency(item.nextMinimumAmountCents ?? 0)} min starts ${formatDate(item.nextMinimumEffectiveDate)})`
                                : "Percentage applied"
                            : item.appliedMethod === "minimum"
                              ? "Minimum applied"
                              : "\u2014"}
                    </td>
                </tr>
            ))}
        </tbody>
        <tfoot>
            <tr className="border-t border-gray-200">
                <td
                    colSpan={4}
                    className="py-1.5 px-2.5 text-xs font-semibold text-right">
                    Total
                </td>
                <td className="py-1.5 px-2.5 text-xs font-bold text-right text-purple-700">
                    {formatCurrency(
                        franchisee.feeStructureBreakdown.reduce(
                            (sum, item) => sum + item.calculatedRoyaltyCents,
                            0,
                        ),
                    )}
                </td>
                <td />
            </tr>
        </tfoot>
    </table>
);

const MultiMonthBreakdownTable: React.FC<{ franchisee: Franchisee }> = ({
    franchisee,
}) => (
    <table className="w-full mb-4">
        <thead>
            <tr>
                <th className={`${thClass} text-left`}>Month</th>
                <th className={`${thClass} text-right`}>Revenue</th>
                <th className={`${thClass} text-right`}>Min Fee</th>
                <th className={`${thClass} text-right`}>Calculated Royalty</th>
                <th className={`${thClass} text-left`}>Applied</th>
            </tr>
        </thead>
        <tbody>
            {franchisee.feeStructureBreakdown.map((item, idx) => (
                <React.Fragment key={idx}>
                    <tr className="bg-gray-50/50">
                        <td
                            colSpan={5}
                            className="py-1.5 px-2.5 text-xs font-semibold text-gray-700">
                            {item.name}
                            <span className="text-gray-400 font-normal ml-2">
                                {humanReadableMode(item.mode)}
                                {item.rateBasisPoints !== null &&
                                    ` \u00b7 ${(item.rateBasisPoints / 100).toFixed(0)}%`}
                            </span>
                        </td>
                    </tr>
                    {item.monthlyDetails.map(detail => (
                        <tr key={detail.month} className="hover:bg-blue-50/20">
                            <td className="py-1 px-2.5 pl-6 text-xs text-gray-600">
                                {formatMonthLabel(detail.month)}
                            </td>
                            <td className="py-1 px-2.5 text-xs text-right text-gray-600">
                                {formatCurrency(detail.revenueCents)}
                            </td>
                            <td className="py-1 px-2.5 text-xs text-right text-gray-600">
                                {detail.minimumFeeCents > 0
                                    ? formatCurrency(detail.minimumFeeCents)
                                    : "\u2014"}
                            </td>
                            <td className="py-1 px-2.5 text-xs text-right font-medium text-purple-700">
                                {formatCurrency(detail.calculatedRoyaltyCents)}
                            </td>
                            <td className="py-1 px-2.5 text-xs text-gray-600">
                                {detail.appliedMethod === "percent"
                                    ? "Percentage"
                                    : detail.appliedMethod === "minimum"
                                      ? "Minimum"
                                      : "\u2014"}
                            </td>
                        </tr>
                    ))}
                    <tr className="border-t border-gray-100">
                        <td
                            colSpan={3}
                            className="py-1 px-2.5 text-xs font-medium text-right text-gray-500">
                            Subtotal
                        </td>
                        <td className="py-1 px-2.5 text-xs text-right font-semibold text-purple-700">
                            {formatCurrency(item.calculatedRoyaltyCents)}
                        </td>
                        <td />
                    </tr>
                </React.Fragment>
            ))}
        </tbody>
        <tfoot>
            <tr className="border-t border-gray-200">
                <td
                    colSpan={3}
                    className="py-1.5 px-2.5 text-xs font-semibold text-right">
                    Total
                </td>
                <td className="py-1.5 px-2.5 text-xs font-bold text-right text-purple-700">
                    {formatCurrency(
                        franchisee.feeStructureBreakdown.reduce(
                            (sum, item) => sum + item.calculatedRoyaltyCents,
                            0,
                        ),
                    )}
                </td>
                <td />
            </tr>
        </tfoot>
    </table>
);

const FeeStructureBreakdownTable: React.FC<{ franchisee: Franchisee }> = ({
    franchisee,
}) => {
    if (hasMultipleMonths(franchisee)) {
        return <MultiMonthBreakdownTable franchisee={franchisee} />;
    }
    return <SingleMonthBreakdownTable franchisee={franchisee} />;
};

const ProgressBar: React.FC<{
    value: number;
    max: number;
    className: string;
}> = ({ value, max, className }) => {
    const width = max > 0 ? (value / max) * 100 : 0;
    return (
        <div className="w-full rounded-full mt-1 h-1 bg-gray-200">
            <div
                className={`rounded-full h-1 ${className}`}
                style={{ width: `${Math.min(width, 100)}%` }}
            />
        </div>
    );
};

const InvoiceDetailSection: React.FC<{ franchisee: Franchisee }> = ({ franchisee }) => {
    const f = franchisee;
    return (
        <tr>
            <td
                colSpan={8}
                className="p-0 bg-blue-50/30 border-t border-b-2 border-blue-100">
                <div className="py-3 px-8 pl-12">
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 pb-1.5 border-b border-gray-200 mb-2">
                        Fee Structure Breakdown
                    </div>
                    {f.hasFeeStructures ? (
                        <FeeStructureBreakdownTable franchisee={f} />
                    ) : (
                        <div className="text-xs text-gray-400 mb-4 py-2">
                            No fee structures configured
                        </div>
                    )}
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 pb-1.5 border-b border-gray-200 mb-2">
                        {f.regionName} — Invoice Detail &middot; {f.invoices.length}{" "}
                        invoice
                        {f.invoices.length !== 1 ? "s" : ""} shown
                    </div>
                    <table className="w-full">
                        <thead>
                            <tr>
                                <th className="text-xs font-semibold uppercase tracking-wide text-gray-500 py-1 px-2.5 text-left border-b border-gray-200">
                                    Invoice #
                                </th>
                                <th className="text-xs font-semibold uppercase tracking-wide text-gray-500 py-1 px-2.5 text-left border-b border-gray-200">
                                    Client
                                </th>
                                <th className="text-xs font-semibold uppercase tracking-wide text-gray-500 py-1 px-2.5 text-left border-b border-gray-200">
                                    Community
                                </th>
                                <th className="text-xs font-semibold uppercase tracking-wide text-gray-500 py-1 px-2.5 text-left border-b border-gray-200">
                                    Assigned To
                                </th>
                                <th className="text-xs font-semibold uppercase tracking-wide text-gray-500 py-1 px-2.5 text-left border-b border-gray-200">
                                    Moved Date
                                </th>
                                <th className="text-xs font-semibold uppercase tracking-wide text-gray-500 py-1 px-2.5 text-left border-b border-gray-200">
                                    Sent Date
                                </th>
                                <th className="text-xs font-semibold uppercase tracking-wide text-gray-500 py-1 px-2.5 text-left border-b border-gray-200">
                                    Due Date
                                </th>
                                <th className="text-xs font-semibold uppercase tracking-wide text-gray-500 py-1 px-2.5 text-left border-b border-gray-200">
                                    Paid Date
                                </th>
                                <th className="text-xs font-semibold uppercase tracking-wide text-gray-500 py-1 px-2.5 text-left border-b border-gray-200">
                                    Status
                                </th>
                                <th className="text-xs font-semibold uppercase tracking-wide text-gray-500 py-1 px-2.5 text-right border-b border-gray-200">
                                    Amount
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {f.invoices.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={10}
                                        className="text-center text-gray-400 py-4 text-xs">
                                        No invoices found in this period.
                                    </td>
                                </tr>
                            ) : (
                                f.invoices.map((inv: InvoiceDetail) => (
                                    <tr
                                        key={inv.invoiceId}
                                        className="hover:bg-blue-50/20">
                                        <td className="py-1.5 px-2.5 text-xs font-mono">
                                            {inv.serial}
                                        </td>
                                        <td className="py-1.5 px-2.5 text-xs">
                                            {inv.clientName}
                                        </td>
                                        <td className="py-1.5 px-2.5 text-xs text-gray-600">
                                            {inv.communityName ?? "—"}
                                        </td>
                                        <td className="py-1.5 px-2.5 text-xs text-gray-600">
                                            {inv.assignedUserName ?? "—"}
                                        </td>
                                        <td className="py-1.5 px-2.5 text-xs">
                                            {formatDate(inv.movedDate)}
                                        </td>
                                        <td className="py-1.5 px-2.5 text-xs">
                                            {formatDate(inv.sentDate)}
                                        </td>
                                        <td className="py-1.5 px-2.5 text-xs">
                                            {formatDate(inv.dueDate)}
                                        </td>
                                        <td className="py-1.5 px-2.5 text-xs">
                                            {formatDate(inv.paidDate)}
                                        </td>
                                        <td className="py-1.5 px-2.5 text-xs">
                                            <StatusBadge status={inv.status} />
                                        </td>
                                        <td className="py-1.5 px-2.5 text-xs text-right font-medium">
                                            {formatCurrency(inv.amountCents)}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                    <div className="flex gap-6 mt-2.5 pt-2.5 border-t border-gray-200 justify-end">
                        <div className="text-right">
                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                Paid Invoices
                            </div>
                            <div className="text-sm font-bold mt-0.5">
                                {f.paidInvoiceCount}
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                Total Collected
                            </div>
                            <div className="text-sm font-bold mt-0.5">
                                {formatCurrency(f.collectedCents)}
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                Fee Structure
                            </div>
                            <div className="text-sm mt-0.5">
                                <FeeStructureSummary franchisee={f} />
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                Royalty Owed
                            </div>
                            <div className="text-sm font-bold mt-0.5 text-purple-700">
                                {formatCurrency(f.royaltyOwedCents)}
                            </div>
                        </div>
                    </div>
                </div>
            </td>
        </tr>
    );
};

export const FranchiseRoyalties: React.FC = () => {
    const isEnabled = useFeature("franchiseManagement");
    const canView = useIsAllowedInWholeTenant(ActionType.ViewFranchiseRoyaltyReport);
    const regionId = useRegionId();
    const dispatch = useAppDispatch();
    const reportState = useAppSelector(state => state.franchiseRoyaltyReport);

    const startDate = reportState.startDate;
    const endDate = reportState.endDate;
    const searchText = reportState.searchText;
    const territoryFilter = reportState.territoryFilter;
    const showOnlyUnderMinimum = reportState.showOnlyUnderMinimum;
    const sortField = reportState.sortField;
    const sortAsc = reportState.sortAsc;

    const updateState = (partial: Partial<typeof reportState>) => {
        dispatch(franchiseRoyaltyReportSlice.actions.update(partial));
    };

    const [expandedRows, setExpandedRows] = useState<Set<string>>(() => new Set());

    const toggleRow = (rowId: string) => {
        setExpandedRows(prev => {
            const next = new Set(prev);
            if (next.has(rowId)) {
                next.delete(rowId);
            } else {
                next.add(rowId);
            }
            return next;
        });
    };

    useEffect(() => {
        setExpandedRows(new Set());
    }, [startDate, endDate, searchText, territoryFilter, showOnlyUnderMinimum]);

    const trpcClient = trpc.useTRPC();

    const reportQuery = useQuery(
        trpcClient.franchiseRoyaltyReport.franchiseRoyaltyReport.queryOptions(
            {
                startDate,
                endDate,
                regionId: regionId || "",
            },
            { enabled: !!regionId },
        ),
    );

    const totalFranchiseeCount = reportQuery.data?.franchisees.length ?? 0;

    const filteredFranchisees = useMemo(() => {
        if (!reportQuery.data) return [];
        let franchisees = reportQuery.data.franchisees;
        if (searchText) {
            const lower = searchText.toLowerCase();
            franchisees = franchisees.filter(
                f =>
                    f.regionName.toLowerCase().includes(lower) ||
                    (f.territoryCode && f.territoryCode.toLowerCase().includes(lower)),
            );
        }
        if (territoryFilter !== "all") {
            franchisees = franchisees.filter(f => f.territoryCode === territoryFilter);
        }
        if (showOnlyUnderMinimum) {
            franchisees = franchisees.filter(f => isUnderMinimum(f));
        }
        return franchisees;
    }, [reportQuery.data, searchText, territoryFilter, showOnlyUnderMinimum]);

    const sortedFranchisees = useMemo(() => {
        return [...filteredFranchisees].sort((a, b) => {
            if (sortField === "regionName" || sortField === "territoryCode") {
                const aStr = (a[sortField] ?? "").toLowerCase();
                const bStr = (b[sortField] ?? "").toLowerCase();
                return sortAsc ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
            }
            const aNum =
                sortField === "avgPerInvoice" ? getAvgPerInvoice(a) : a[sortField];
            const bNum =
                sortField === "avgPerInvoice" ? getAvgPerInvoice(b) : b[sortField];
            return sortAsc ? aNum - bNum : bNum - aNum;
        });
    }, [filteredFranchisees, sortField, sortAsc]);

    const summary = useMemo(() => {
        const regionIds = new Set(filteredFranchisees.map(f => f.regionId));
        const activeRegionIds = new Set(
            filteredFranchisees.filter(f => f.paidInvoiceCount > 0).map(f => f.regionId),
        );
        const franchiseeCount = regionIds.size;
        const activeFranchiseeCount = activeRegionIds.size;
        const totalPaidInvoices = filteredFranchisees.reduce(
            (s, f) => s + f.paidInvoiceCount,
            0,
        );
        const totalCollectedCents = filteredFranchisees.reduce(
            (s, f) => s + f.collectedCents,
            0,
        );
        const totalRoyaltyOwedCents = filteredFranchisees.reduce(
            (s, f) => s + f.royaltyOwedCents,
            0,
        );
        const avgRoyaltyPerFranchisee =
            activeFranchiseeCount > 0
                ? Math.round(totalRoyaltyOwedCents / activeFranchiseeCount)
                : 0;

        return {
            franchiseeCount,
            activeFranchiseeCount,
            territoryCount: filteredFranchisees.length,
            totalPaidInvoices,
            totalCollectedCents,
            totalRoyaltyOwedCents,
            avgRoyaltyPerFranchisee,
        };
    }, [filteredFranchisees]);

    const territoryOptions = useMemo(() => {
        if (!reportQuery.data) return [];
        const codes = reportQuery.data.franchisees
            .map(f => f.territoryCode)
            .filter((code): code is string => !!code);
        return Array.from(new Set(codes)).sort();
    }, [reportQuery.data]);

    const maxValues = useMemo(() => {
        return {
            paidInvoices: Math.max(
                ...filteredFranchisees.map(f => f.paidInvoiceCount),
                1,
            ),
            collected: Math.max(...filteredFranchisees.map(f => f.collectedCents), 1),
            avgPerInvoice: Math.max(
                ...filteredFranchisees.map(f => getAvgPerInvoice(f)),
                1,
            ),
            royaltyOwed: Math.max(...filteredFranchisees.map(f => f.royaltyOwedCents), 1),
        };
    }, [filteredFranchisees]);

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            updateState({ sortAsc: !sortAsc });
        } else {
            updateState({ sortField: field, sortAsc: false });
        }
    };

    const sortIndicator = (field: SortField) => {
        if (sortField !== field) return null;
        return sortAsc ? (
            <SortUp className="inline ml-1" />
        ) : (
            <SortDown className="inline ml-1" />
        );
    };

    const startDateValue = CalendarDate.parse(startDate).getOrElse(CalendarDate.today);
    const endDateValue = CalendarDate.parse(endDate).getOrElse(CalendarDate.today);

    if (!isEnabled || !canView) return null;

    return (
        <Stage>
            <PageHeader title="Franchise Royalties" stackBreakpoint="md">
                <DateRangeControl
                    value={{
                        startDate: startDateValue,
                        endDate: endDateValue,
                        activePreset: reportState.activePreset,
                    }}
                    onChange={v => {
                        updateState({
                            startDate: v.startDate.toString(),
                            endDate: v.endDate.toString(),
                            activePreset: v.activePreset,
                        });
                    }}
                />
            </PageHeader>
            <ContentContainer>
                {reportQuery.isLoading && (
                    <div className="flex justify-center py-12">
                        <Spinner />
                    </div>
                )}

                {reportQuery.isError && (
                    <Error componentName="FranchiseRoyalties">
                        {(reportQuery.error as { message?: string })?.message ??
                            "Failed to load report. Please try again."}
                    </Error>
                )}

                {reportQuery.data && (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
                            <div className={`${defaultCardStyle} p-3`}>
                                <div className="text-xs text-gray-500 uppercase font-medium">
                                    Franchisees
                                </div>
                                <div className="text-2xl font-bold mt-1">
                                    {summary.franchiseeCount}
                                </div>
                            </div>
                            <div className={`${defaultCardStyle} p-3`}>
                                <div className="text-xs text-gray-500 uppercase font-medium">
                                    Territories
                                </div>
                                <div className="text-2xl font-bold mt-1">
                                    {summary.territoryCount}
                                </div>
                            </div>
                            <div className={`${defaultCardStyle} p-3`}>
                                <div className="text-xs text-blue-700 uppercase font-medium">
                                    Paid Invoices
                                </div>
                                <div className="text-2xl font-bold mt-1">
                                    {summary.totalPaidInvoices}
                                </div>
                            </div>
                            <div className={`${defaultCardStyle} p-3`}>
                                <div className="text-xs text-green-700 uppercase font-medium">
                                    Total Collected
                                </div>
                                <div className="text-2xl font-bold mt-1">
                                    {formatCurrency(summary.totalCollectedCents)}
                                </div>
                                <div className="text-xs text-gray-500">
                                    across {summary.activeFranchiseeCount} active
                                    franchisees
                                </div>
                            </div>
                            <div className={`${defaultCardStyle} p-3`}>
                                <div className="text-xs text-purple-700 uppercase font-medium">
                                    Total Royalties Owed
                                </div>
                                <div className="text-2xl font-bold mt-1">
                                    {formatCurrency(summary.totalRoyaltyOwedCents)}
                                </div>
                            </div>
                            <div className={`${defaultCardStyle} p-3`}>
                                <div className="text-xs text-orange-700 uppercase font-medium">
                                    Avg Royalty / Franchisee
                                </div>
                                <div className="text-2xl font-bold mt-1">
                                    {formatCurrency(summary.avgRoyaltyPerFranchisee)}
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-4 mb-4">
                            <div className="flex-1">
                                <SearchInput
                                    label="Search Franchisee / Territory"
                                    value={searchText}
                                    onChange={e =>
                                        updateState({
                                            searchText: e.target.value,
                                        })
                                    }
                                    onClear={() => updateState({ searchText: "" })}
                                />
                            </div>
                            <div className="w-full sm:w-64">
                                <Select
                                    label="Territory"
                                    value={territoryFilter}
                                    onChange={e =>
                                        updateState({
                                            territoryFilter: e.target.value,
                                        })
                                    }>
                                    <option value="all">All Territories</option>
                                    {territoryOptions.map(code => (
                                        <option key={code} value={code}>
                                            {code}
                                        </option>
                                    ))}
                                </Select>
                            </div>
                            <div className="flex items-end pb-1">
                                <Checkbox
                                    label="Under minimum only"
                                    checked={showOnlyUnderMinimum}
                                    onChange={e =>
                                        updateState({
                                            showOnlyUnderMinimum: e.target.checked,
                                        })
                                    }
                                />
                            </div>
                        </div>

                        <div className="text-sm text-gray-500 mb-2">
                            Showing {sortedFranchisees.length} of {totalFranchiseeCount}{" "}
                            territories
                        </div>

                        <div className={`${defaultCardStyle} overflow-x-auto`}>
                            {sortedFranchisees.length === 0 ? (
                                <div className="text-center py-10 text-gray-500">
                                    No territories match your current filters.
                                </div>
                            ) : (
                                <table className="min-w-full">
                                    <thead>
                                        <tr className="bg-gray-50 border-b border-gray-200">
                                            <th
                                                className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-100"
                                                onClick={() => handleSort("regionName")}>
                                                Franchisee
                                                {sortIndicator("regionName")}
                                            </th>
                                            <th
                                                className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-100"
                                                onClick={() =>
                                                    handleSort("territoryCode")
                                                }>
                                                Territory
                                                {sortIndicator("territoryCode")}
                                            </th>
                                            <th
                                                className="py-3 px-4 text-center text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-100"
                                                onClick={() => handleSort("userCount")}>
                                                Users
                                                {sortIndicator("userCount")}
                                            </th>
                                            <th
                                                className="py-3 px-4 text-right text-xs font-semibold text-blue-700 uppercase cursor-pointer hover:bg-gray-100"
                                                onClick={() =>
                                                    handleSort("paidInvoiceCount")
                                                }>
                                                Paid Invoices
                                                {sortIndicator("paidInvoiceCount")}
                                            </th>
                                            <th
                                                className="py-3 px-4 text-right text-xs font-semibold text-green-700 uppercase cursor-pointer hover:bg-gray-100"
                                                onClick={() =>
                                                    handleSort("collectedCents")
                                                }>
                                                Collected
                                                {sortIndicator("collectedCents")}
                                            </th>
                                            <th
                                                className="py-3 px-4 text-right text-xs font-semibold text-orange-700 uppercase cursor-pointer hover:bg-gray-100"
                                                onClick={() =>
                                                    handleSort("avgPerInvoice")
                                                }>
                                                Avg/Invoice
                                                {sortIndicator("avgPerInvoice")}
                                            </th>
                                            <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase">
                                                Fee Structure
                                            </th>
                                            <th
                                                className="py-3 px-4 text-right text-xs font-semibold text-purple-700 uppercase cursor-pointer hover:bg-gray-100"
                                                onClick={() =>
                                                    handleSort("royaltyOwedCents")
                                                }>
                                                Royalty Owed
                                                {sortIndicator("royaltyOwedCents")}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedFranchisees.map(f => {
                                            const avgInv = getAvgPerInvoice(f);
                                            const isExpanded = expandedRows.has(
                                                f.territoryId,
                                            );
                                            return (
                                                <React.Fragment key={f.territoryId}>
                                                    <tr
                                                        className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${isExpanded ? "bg-gray-50" : ""}`}
                                                        onClick={() =>
                                                            toggleRow(f.territoryId)
                                                        }>
                                                        <td className="py-2.5 px-4 text-sm font-medium text-blue-700">
                                                            <span className="inline-flex items-center gap-1">
                                                                {isExpanded ? (
                                                                    <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                                                                ) : (
                                                                    <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                                                                )}
                                                                {f.regionName}
                                                            </span>
                                                        </td>
                                                        <td className="py-2.5 px-4 text-sm text-gray-700">
                                                            {f.territoryCode ?? "—"}
                                                        </td>
                                                        <td className="py-2.5 px-4 text-center text-sm font-medium">
                                                            {f.userCount}
                                                        </td>
                                                        <td className="py-2.5 px-4 text-right">
                                                            <div className="font-medium text-sm">
                                                                {f.paidInvoiceCount}
                                                            </div>
                                                            <ProgressBar
                                                                value={f.paidInvoiceCount}
                                                                max={
                                                                    maxValues.paidInvoices
                                                                }
                                                                className="bg-blue-600"
                                                            />
                                                        </td>
                                                        <td className="py-2.5 px-4 text-right">
                                                            <div className="font-medium text-sm">
                                                                {formatCurrency(
                                                                    f.collectedCents,
                                                                )}
                                                            </div>
                                                            <ProgressBar
                                                                value={f.collectedCents}
                                                                max={maxValues.collected}
                                                                className="bg-green-600"
                                                            />
                                                        </td>
                                                        <td className="py-2.5 px-4 text-right">
                                                            <div className="font-medium text-sm text-orange-700">
                                                                {f.paidInvoiceCount > 0
                                                                    ? formatCurrency(
                                                                          avgInv,
                                                                      )
                                                                    : "—"}
                                                            </div>
                                                            {f.paidInvoiceCount > 0 && (
                                                                <ProgressBar
                                                                    value={avgInv}
                                                                    max={
                                                                        maxValues.avgPerInvoice
                                                                    }
                                                                    className="bg-orange-600"
                                                                />
                                                            )}
                                                        </td>
                                                        <td className="py-2.5 px-4 text-sm">
                                                            <FeeStructureSummary
                                                                franchisee={f}
                                                            />
                                                        </td>
                                                        <td className="py-2.5 px-4 text-right">
                                                            <div className="font-medium text-sm text-purple-700">
                                                                {formatCurrency(
                                                                    f.royaltyOwedCents,
                                                                )}
                                                            </div>
                                                            <ProgressBar
                                                                value={f.royaltyOwedCents}
                                                                max={
                                                                    maxValues.royaltyOwed
                                                                }
                                                                className="bg-purple-600"
                                                            />
                                                        </td>
                                                    </tr>
                                                    {isExpanded && (
                                                        <InvoiceDetailSection
                                                            franchisee={f}
                                                        />
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                        <tr className="bg-gray-50 font-semibold border-t-2 border-gray-200">
                                            <td className="py-3 px-4 text-sm">
                                                Total ({sortedFranchisees.length}{" "}
                                                territories)
                                            </td>
                                            <td className="py-3 px-4" />
                                            <td className="py-3 px-4" />
                                            <td className="py-3 px-4 text-right text-sm">
                                                {summary.totalPaidInvoices}
                                            </td>
                                            <td className="py-3 px-4 text-right text-sm">
                                                {formatCurrency(
                                                    summary.totalCollectedCents,
                                                )}
                                            </td>
                                            <td className="py-3 px-4" />
                                            <td className="py-3 px-4" />
                                            <td className="py-3 px-4" />
                                            <td className="py-3 px-4 text-right text-sm text-purple-700">
                                                {formatCurrency(
                                                    summary.totalRoyaltyOwedCents,
                                                )}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            )}
                        </div>

                        <div className="mt-4 space-y-1 text-xs text-gray-400">
                            <p>
                                Only Paid invoices are included in royalty calculations,
                                based on the date paid falling within the selected date
                                range.
                            </p>
                            <p>
                                Royalties are calculated based on each franchisee&apos;s
                                configured fee structures.
                            </p>
                            <p>
                                Click any franchisee row to expand and view individual
                                invoice detail.
                            </p>
                        </div>
                    </>
                )}
            </ContentContainer>
        </Stage>
    );
};
